import React, { useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause } from "lucide-react";
import { useLanguage } from "../i18n";
import { GuidedTutorial } from "./GuidedTutorial";
import { RequestKindToggle, stepsFor } from "./RequestKindToggle";

// GuidedTutorial scenes: service, profile, space, mood, upload + consent, confirmation, delivery -> steps 1-4
const SCENE_TO_STEP = [1, 1, 1, 1, 0, 2, 3];

export const VideoTutorial = () => {
  const { t } = useLanguage();
  const [playing, setPlaying] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [kind, setKind] = useState("concept");

  return (
    <section id="tutorial" data-testid="tutorial-section" className="py-24 lg:py-32 bg-sand/60">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <motion.div
          className="lg:col-span-6 order-2 lg:order-1"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          <div data-testid="tutorial-video-frame" className="relative rounded-sm border border-ink/10 shadow-[0_24px_60px_rgba(42,39,38,0.12)] overflow-hidden bg-espresso">
            <div className="relative aspect-video">
              <GuidedTutorial playing={playing} kind={kind} onStep={(s) => setActiveStep(SCENE_TO_STEP[s])} />
              {!playing ? (
                <button
                  data-testid="tutorial-video-play"
                  onClick={() => setPlaying(true)}
                  aria-label={t.tutorial.videoCaption}
                  className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-espresso/45 hover:bg-espresso/35 transition-colors group"
                >
                  <span className="w-16 h-16 rounded-full bg-cream/95 flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
                    <Play className="w-6 h-6 text-ink ml-1" fill="currentColor" />
                  </span>
                  <span className="text-cream text-xs font-semibold uppercase tracking-[0.2em]">{t.tutorial.videoCaption}</span>
                </button>
              ) : (
                <button
                  data-testid="tutorial-video-pause"
                  onClick={() => setPlaying(false)}
                  aria-label="Pausa"
                  className="absolute bottom-3 left-3 w-9 h-9 rounded-full bg-cream/90 hover:bg-cream flex items-center justify-center shadow transition-colors"
                >
                  <Pause className="w-4 h-4 text-ink" fill="currentColor" />
                </button>
              )}
            </div>
          </div>
        </motion.div>

        <motion.div
          className="lg:col-span-6 order-1 lg:order-2"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-amberdark mb-6">{t.tutorial.overline}</p>
          <h2 data-testid="tutorial-title" className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-tight text-ink">
            {t.tutorial.title}
          </h2>
          <p className="mt-5 text-base text-inksoft leading-relaxed max-w-xl">{t.tutorial.subtitle}</p>
          <div className="mt-8"><RequestKindToggle t={t} value={kind} onChange={setKind} testid="tutorial-kind" /></div>
          <ol className="mt-8 space-y-5">
            {stepsFor(t, kind).map((step, i) => {
              const active = playing && activeStep === i;
              return (
                <li key={i} data-testid={`tutorial-step-${i}`} className={`flex items-start gap-5 rounded-sm p-3 -mx-3 transition-colors duration-500 ${active ? "bg-white shadow-[0_8px_30px_rgba(42,39,38,0.06)]" : ""}`}>
                  <span className={`font-display text-3xl leading-none shrink-0 w-10 transition-colors ${active ? "text-ink" : "text-amberdark"}`}>{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-[0.12em] text-ink">{step.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-inksoft">{step.desc}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        </motion.div>
      </div>
    </section>
  );
};
